import React from "react";
import { useTranslation, Trans } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Atom,
  Zap,
  Heart,
  Shield,
  Sparkles,
  Dumbbell,
  Moon,
  Battery,
  Clock,
  FlaskConical,
  Lightbulb,
  Target,
  CheckCircle2,
  Info,
  TrendingUp,
  BookOpen,
  ArrowRight,
} from "lucide-react";
import { PageNavigation, PageBreadcrumb } from "@/components/PageNavigation";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

// Use cases with icons
const USE_CASES = [
  { icon: TrendingUp, labelKey: "useCase1", descKey: "useCase1Desc", color: "from-emerald-500/20 to-green-500/20", iconColor: "text-emerald-600" },
  { icon: Dumbbell, labelKey: "useCase2", descKey: "useCase2Desc", color: "from-blue-500/20 to-cyan-500/20", iconColor: "text-blue-600" },
  { icon: Moon, labelKey: "useCase3", descKey: "useCase3Desc", color: "from-indigo-500/20 to-violet-500/20", iconColor: "text-indigo-600" },
  { icon: Battery, labelKey: "useCase4", descKey: "useCase4Desc", color: "from-amber-500/20 to-orange-500/20", iconColor: "text-amber-600" },
  { icon: Sparkles, labelKey: "useCase5", descKey: "useCase5Desc", color: "from-pink-500/20 to-rose-500/20", iconColor: "text-pink-600" },
  { icon: Clock, labelKey: "useCase6", descKey: "useCase6Desc", color: "from-teal-500/20 to-cyan-500/20", iconColor: "text-teal-600" },
];

// Hero illustration - Animated peptide chain
const PeptideChainIllustration = ({ t }) => (
  <div className="relative w-full max-w-2xl mx-auto py-8">
    <svg viewBox="0 0 500 140" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
      {/* Background gradient */}
      <defs>
        <linearGradient id="chainGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(201, 59%, 40%)" stopOpacity="0.2" />
          <stop offset="50%" stopColor="hsl(201, 59%, 50%)" stopOpacity="0.4" />
          <stop offset="100%" stopColor="hsl(201, 59%, 40%)" stopOpacity="0.2" />
        </linearGradient>
        <linearGradient id="nodeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="hsl(201, 59%, 55%)" />
          <stop offset="100%" stopColor="hsl(201, 59%, 35%)" />
        </linearGradient>
      </defs>
      
      {/* Connection lines */}
      <path d="M 60 70 Q 100 40, 140 70 Q 180 100, 220 70 Q 260 40, 300 70 Q 340 100, 380 70 Q 420 40, 460 70" 
            stroke="url(#chainGradient)" strokeWidth="3" fill="none" strokeLinecap="round"/>
      
      {/* Amino acid nodes */}
      {[60, 140, 220, 300, 380, 460].map((x, i) => (
        <g key={i}>
          <circle cx={x} cy={70} r={22} fill="hsl(201, 59%, 50%)" opacity="0.1" />
          <circle cx={x} cy={70} r={16} fill="url(#nodeGradient)" opacity="0.8" />
          <circle cx={x} cy={66} r={8} fill="white" opacity="0.3" />
          <text x={x} y={74} fontSize="10" fill="white" textAnchor="middle" fontWeight="bold">
            {['A', 'M', 'I', 'N', 'O', 'S'][i]}
          </text>
        </g>
      ))}
      
      {/* Labels */}
      <text x="60" y="115" fontSize="11" fill="hsl(201, 30%, 45%)" textAnchor="middle">{t('whatArePeptides.amino')}</text>
      <text x="460" y="115" fontSize="11" fill="hsl(201, 30%, 45%)" textAnchor="middle">{t('whatArePeptides.acids')}</text>
      <text x="260" y="25" fontSize="12" fill="hsl(201, 59%, 40%)" textAnchor="middle" fontWeight="600">
        {t('whatArePeptides.peptideChainLabel')}
      </text>
    </svg>
  </div>
);

// Improved Key and Lock illustration - matching reference image style
const KeyLockIllustration = ({ t }) => (
  <svg viewBox="0 0 320 160" className="w-full h-auto max-w-[300px]" xmlns="http://www.w3.org/2000/svg">
    <defs>
      {/* Gradients for the peptide key */}
      <linearGradient id="keyHeadGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="hsl(201, 50%, 70%)" />
        <stop offset="100%" stopColor="hsl(201, 50%, 55%)" />
      </linearGradient>
      <linearGradient id="keyShaftGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="hsl(201, 45%, 65%)" />
        <stop offset="100%" stopColor="hsl(201, 45%, 50%)" />
      </linearGradient>
      {/* Gradient for the receptor */}
      <linearGradient id="receptorGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="hsl(201, 20%, 95%)" />
        <stop offset="100%" stopColor="hsl(201, 20%, 85%)" />
      </linearGradient>
      <linearGradient id="receptorInnerGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="hsl(201, 30%, 90%)" />
        <stop offset="100%" stopColor="hsl(201, 25%, 80%)" />
      </linearGradient>
    </defs>
    
    {/* Peptide Key */}
    <g transform="translate(25, 35)">
      {/* Key head - oval shape with inner circle */}
      <ellipse cx="35" cy="35" rx="32" ry="28" fill="url(#keyHeadGradient)" opacity="0.7"/>
      <ellipse cx="35" cy="35" rx="22" ry="18" fill="hsl(201, 50%, 60%)" opacity="0.5"/>
      {/* Inner highlight circle */}
      <ellipse cx="32" cy="30" rx="12" ry="10" fill="white" opacity="0.35"/>
      
      {/* Key shaft */}
      <rect x="60" y="25" width="65" height="20" rx="3" fill="url(#keyShaftGradient)" opacity="0.7"/>
      
      {/* Key teeth/notches */}
      <rect x="90" y="17" width="10" height="12" rx="2" fill="hsl(201, 45%, 55%)" opacity="0.8"/>
      <rect x="108" y="20" width="10" height="9" rx="2" fill="hsl(201, 45%, 55%)" opacity="0.8"/>
      
      {/* Label */}
      <text x="35" y="95" fontSize="12" fill="hsl(201, 30%, 40%)" textAnchor="middle" fontWeight="500">{t('whatArePeptides.peptide')}</text>
    </g>
    
    {/* Arrow indicating movement */}
    <g transform="translate(145, 65)">
      <line x1="0" y1="5" x2="25" y2="5" stroke="hsl(201, 50%, 50%)" strokeWidth="2" strokeDasharray="4 3"/>
      <polygon points="25,5 18,0 18,10" fill="hsl(201, 50%, 50%)"/>
    </g>
    
    {/* Cell Receptor */}
    <g transform="translate(180, 15)">
      {/* Outer receptor body */}
      <rect x="0" y="0" width="110" height="100" rx="12" fill="url(#receptorGradient)" stroke="hsl(201, 30%, 75%)" strokeWidth="2"/>
      
      {/* Inner receptor pocket/cavity */}
      <rect x="15" y="25" width="80" height="50" rx="8" fill="white" stroke="hsl(201, 40%, 60%)" strokeWidth="2"/>
      
      {/* Lock mechanism inside */}
      {/* Circular lock body */}
      <circle cx="55" cy="42" r="12" fill="hsl(201, 50%, 60%)" opacity="0.25"/>
      <circle cx="55" cy="42" r="8" fill="white" opacity="0.4"/>
      
      {/* Keyhole shape */}
      <rect x="51" y="48" width="8" height="18" rx="2" fill="hsl(201, 50%, 60%)" opacity="0.3"/>
      
      {/* Label */}
      <text x="55" y="120" fontSize="12" fill="hsl(201, 30%, 40%)" textAnchor="middle" fontWeight="500">{t('whatArePeptides.cellReceptor')}</text>
    </g>
  </svg>
);

// Comparison illustration - Protein vs Peptide
const ProteinVsPeptideIllustration = ({ t }) => (
  <div className="grid grid-cols-2 gap-6 max-w-md mx-auto">
    <div className="text-center">
      <div className="bg-muted/30 rounded-xl p-4 mb-2">
        <svg viewBox="0 0 80 80" className="w-16 h-16 mx-auto" xmlns="http://www.w3.org/2000/svg">
          <path d="M 40 10 Q 60 20, 50 40 Q 40 60, 20 50 Q 10 40, 30 30 Q 50 20, 40 10" 
                fill="none" stroke="hsl(201, 59%, 50%)" strokeWidth="2" opacity="0.6"/>
          <path d="M 35 15 Q 55 25, 45 45 Q 35 55, 25 45 Q 20 35, 35 30 Q 50 25, 35 15" 
                fill="hsl(201, 59%, 50%)" opacity="0.15"/>
          {[[40,10], [55,25], [50,45], [30,55], [15,40], [25,20]].map(([x,y], i) => (
            <circle key={i} cx={x} cy={y} r={5} fill="hsl(201, 59%, 50%)" opacity="0.5"/>
          ))}
        </svg>
      </div>
      <p className="text-sm font-semibold text-foreground">{t('whatArePeptides.protein')}</p>
      <p className="text-xs text-muted-foreground">{t('whatArePeptides.largeMachine')}</p>
    </div>
    <div className="text-center">
      <div className="bg-primary/10 rounded-xl p-4 mb-2 border-2 border-primary/20">
        <svg viewBox="0 0 80 80" className="w-16 h-16 mx-auto" xmlns="http://www.w3.org/2000/svg">
          <line x1="15" y1="40" x2="65" y2="40" stroke="hsl(201, 59%, 50%)" strokeWidth="2" opacity="0.5"/>
          {[[15,40], [32,40], [48,40], [65,40]].map(([x,y], i) => (
            <circle key={i} cx={x} cy={y} r={7} fill="hsl(201, 59%, 45%)" opacity="0.8"/>
          ))}
        </svg>
      </div>
      <p className="text-sm font-semibold text-primary">{t('whatArePeptides.peptide')}</p>
      <p className="text-xs text-muted-foreground">{t('whatArePeptides.shortMessage')}</p>
    </div>
  </div>
);

export const WhatArePeptidesPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background">
      {/* Top Breadcrumb Navigation */}
      <PageBreadcrumb currentPath="/what-are-peptides" />

      {/* Hero Section */}
      <div className="w-full py-10 md:py-14 px-4 bg-gradient-to-b from-primary/8 via-primary/4 to-background">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Atom className="w-5 h-5 text-primary" />
            </div>
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              {t('whatArePeptides.understandingBasics')}
            </span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3">
            {t('whatArePeptides.title')}
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto">
            {t('whatArePeptides.subtitle')}
          </p>

          {/* Peptide chain visual */}
          <PeptideChainIllustration t={t} />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10 space-y-12">
        
        {/* Introduction Section */}
        <section className="page-slide-up">
          <Card className="overflow-hidden border-l-4 border-l-primary">
            <CardContent className="p-6 md:p-8">
              <p className="text-lg md:text-xl text-foreground leading-relaxed mb-6">
                {t('whatArePeptides.introText')}
              </p>
              
              {/* Visual comparison */}
              <ProteinVsPeptideIllustration t={t} />
              
              <p className="text-muted-foreground leading-relaxed mt-6 mb-4">
                <Trans i18nKey="whatArePeptides.proteinAnalogy" />
              </p>
              
              <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg p-5 mt-6 border border-primary/10">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                    <Heart className="w-4 h-4 text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <Trans i18nKey="whatArePeptides.naturalPeptides" />
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* What do peptides do Section */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-primary/10 rounded-xl">
              <Zap className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">{t('whatArePeptides.whatDoPeptidesDo')}</h2>
          </div>
          
          <div className="grid md:grid-cols-5 gap-6 items-start">
            <div className="md:col-span-3 space-y-4">
              <p className="text-muted-foreground leading-relaxed">
                <Trans i18nKey="whatArePeptides.sendingSignals" />
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t('whatArePeptides.receptorExplanation')}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                <Trans i18nKey="whatArePeptides.specificProcesses" />
              </p>
            </div>
            <div className="md:col-span-2">
              <Card className="p-5 bg-gradient-to-br from-muted/30 to-muted/50 border-0">
                <KeyLockIllustration t={t} />
                <p className="text-xs text-center text-muted-foreground mt-3 italic">
                  {t('whatArePeptides.keyLockCaption')}
                </p>
              </Card>
            </div>
          </div>
        </section>

        <Separator className="my-8" />

        {/* Well Tolerated Section */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-emerald-500/10 rounded-xl">
              <Shield className="w-5 h-5 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">{t('whatArePeptides.whyGentle')}</h2>
          </div>
          
          <p className="text-muted-foreground leading-relaxed mb-6">
            {t('whatArePeptides.wellTolerated')}
          </p>
          
          <Card className="bg-gradient-to-r from-emerald-50/80 to-teal-50/80 border-emerald-200/60">
            <CardContent className="p-6">
              <p className="text-sm font-semibold text-foreground mb-4">{t('whatArePeptides.commonReasons')}</p>
              <ul className="space-y-3">
                {['reason1', 'reason2', 'reason3'].map((key, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{t(`whatArePeptides.${key}`)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          
          <div className="mt-4 p-4 bg-amber-50/80 border border-amber-200/60 rounded-lg flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              <Trans i18nKey="whatArePeptides.notAllSame" />
            </p>
          </div>
        </section>

        {/* What are peptides used for Section */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-primary/10 rounded-xl">
              <Target className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">{t('whatArePeptides.whatUsedFor')}</h2>
          </div>
          
          <p className="text-muted-foreground leading-relaxed mb-6">
            {t('whatArePeptides.usedForIntro')}
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {USE_CASES.map((item, i) => {
              const Icon = item.icon;
              return (
                <Card key={i} className="hover:shadow-md transition-all hover:-translate-y-0.5 border-0 bg-gradient-to-br from-background to-muted/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${item.color}`}>
                        <Icon className={`w-4 h-4 ${item.iconColor}`} />
                      </div>
                      <span className="font-semibold text-foreground text-sm">{t(`whatArePeptides.${item.labelKey}`)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground pl-11">{t(`whatArePeptides.${item.descKey}`)}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          <p className="text-sm text-muted-foreground mt-6 italic">
            {t('whatArePeptides.fdaNote')}
          </p>
        </section>

        <Separator className="my-8" />

        {/* Science Section */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-blue-500/10 rounded-xl">
              <FlaskConical className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">{t('whatArePeptides.scienceSays')}</h2>
          </div>
          
          <div className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              {t('whatArePeptides.scienceText1')}
            </p>
            <p className="text-muted-foreground leading-relaxed">
              {t('whatArePeptides.scienceText2')}
            </p>
            <p className="text-muted-foreground leading-relaxed">
              <Trans i18nKey="whatArePeptides.scienceText3" />
            </p>
          </div>
        </section>

        {/* Why Popular Section */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-amber-500/10 rounded-xl">
              <TrendingUp className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">{t('whatArePeptides.whyPopular')}</h2>
          </div>
          
          <p className="text-muted-foreground leading-relaxed mb-4">
            {t('whatArePeptides.popularIntro')}
          </p>
          
          <ul className="space-y-3 ml-1">
            {['popularReason1', 'popularReason2', 'popularReason3', 'popularReason4'].map((key, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                <span className="text-muted-foreground">{t(`whatArePeptides.${key}`)}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Important Perspective */}
        <section>
          <Card className="bg-gradient-to-br from-primary/5 via-primary/8 to-primary/5 border-primary/20 overflow-hidden">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/15 rounded-lg">
                  <Lightbulb className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-foreground">{t('whatArePeptides.importantPerspective')}</h2>
              </div>
              <p className="text-lg font-semibold text-foreground mb-4">
                {t('whatArePeptides.toolsNotShortcuts')}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t('whatArePeptides.toolsExplanation')}
              </p>
            </CardContent>
          </Card>
        </section>

        <Separator className="my-8" />

        {/* Summary Section */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-primary/10 rounded-xl">
              <BookOpen className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">{t('whatArePeptides.inSimpleTerms')}</h2>
          </div>
          
          <Card className="border-2 border-muted">
            <CardContent className="p-6">
              <ul className="space-y-4">
                {['summary1', 'summary2', 'summary3', 'summary4', 'summary5'].map((key, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/15 mt-0.5 font-bold">
                      {i + 1}
                    </Badge>
                    <span className="text-foreground leading-relaxed">{t(`whatArePeptides.${key}`)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* CTA Section */}
        <section className="pt-4">
          <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-foreground mb-1">{t('whatArePeptides.readyToExplore')}</h3>
                  <p className="text-sm text-muted-foreground">{t('whatArePeptides.readyToExploreDesc')}</p>
                </div>
                <Link to="/learn">
                  <Button className="gap-2 whitespace-nowrap">
                    <BookOpen className="w-4 h-4" />
                    {t('nav.learnPeptides')}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>

      </div>

      {/* Page Navigation */}
      <PageNavigation currentPath="/what-are-peptides" />

      {/* Disclaimer Footer */}
      <div className="border-t border-border/50 bg-muted/20 py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            <strong>{t('common.disclaimer')}</strong> {t('common.disclaimerText')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default WhatArePeptidesPage;
